import { useState } from 'react';
import { useClusterApi } from '../../api/ClusterApiProvider';
import { useAsync } from '../../api/useAsync';
import { toApiError } from '../../api/errors';
import type { ApiError } from '../../api/errors';
import { useClusterSession } from '../../auth/ClusterSessionProvider';
import { authorizationTarget } from '../../auth/clusterSignIn';
import { useI18n } from '../../i18n/useI18n';
import { USING_STUBS } from '../../api';
import { StubBanner, StubClusterProvider } from '../../stubs/StubBanner';
import { Alert, ErrorAlert } from '../../ui/Alert';
import { AuthLayout } from '../../ui/AuthLayout';
import { RedirectUriNotice } from '../../ui/RedirectUriNotice';
import { Button, Loading } from '../../ui/primitives';
import { IconExternal } from '../../ui/icons';

/**
 * The way in to a cluster console: through the identity provider that tenancy
 * chose, and through nothing else.
 *
 * Pressing the button asks the control plane to start a sign-in. It writes down
 * the `state`, the `nonce` and the PKCE verifier under a key it hands this
 * browser as a short-lived cookie, and answers with an address at the tenancy's
 * provider; the browser leaves. It returns to this same screen with a `code`
 * and a `state`, and by the time anything is drawn the session provider has
 * already exchanged them — which is why a failed return is a message on this
 * screen rather than a screen of its own.
 *
 * The address the provider returns to is on this screen too, in full and
 * copyable. It is the one step of the flow nothing here can perform or check:
 * a return address that was not registered at the tenancy's provider is refused
 * by that provider, before the request reaches us, so it appears in no log of
 * ours. Somebody who has just been refused needs to compare the two strings,
 * and this is where they can.
 *
 * Two of the three offers are not failures. A tenancy that has not had a
 * provider applied, and one in the middle of a change, both have a working
 * cluster and nobody who can sign in to it — and in both cases the way out is
 * the account, on another domain, which is why its address is on screen and
 * comes out of the answer rather than out of this file.
 */
export function ClusterLoginScreen({ tenancyDomain }: { tenancyDomain: string }) {
  const { t, formatRelative } = useI18n();
  const api = useClusterApi();
  const { signInFailure } = useClusterSession();

  const state = useAsync((signal) => api.signInOffer(signal), 'sign-in-offer');

  const [starting, setStarting] = useState(false);
  const [startFailure, setStartFailure] = useState<ApiError | null>(null);
  /** Set when the answer named no address this console will follow. */
  const [unusableTarget, setUnusableTarget] = useState(false);

  async function begin() {
    setStartFailure(null);
    setUnusableTarget(false);
    setStarting(true);
    try {
      const started = await api.startSignIn();
      const target = authorizationTarget(started);
      if (target === null) {
        setUnusableTarget(true);
        return;
      }
      // Leaving for somebody else's system. Nothing is kept here: the console
      // has nothing to keep, and comes back to a page that starts from scratch.
      window.location.assign(target);
    } catch (error: unknown) {
      setStartFailure(toApiError(error));
    } finally {
      setStarting(false);
    }
  }

  return (
    <AuthLayout>
      <h1>{t('clusterLogin.heading')}</h1>
      <p className="sub" style={{ marginBottom: 22 }}>
        {t('clusterLogin.subtitle', { domain: tenancyDomain })}
      </p>

      {/* Outside everything below: a return that produced no session has to be
          said whether or not the standing offer could be read afterwards. */}
      {signInFailure !== null && (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <ReturnFailure failure={signInFailure} />
        </div>
      )}

      {state.status === 'loading' && <Loading />}

      {state.status === 'error' && (
        <ErrorAlert
          error={state.error}
          action={
            <div style={{ marginTop: 12 }}>
              <Button size="sm" onClick={state.reload}>
                {t('common.retry')}
              </Button>
            </div>
          }
        />
      )}

      {state.status === 'ready' && (
        <div className="stack">
          {startFailure !== null && <ErrorAlert error={startFailure} />}

          {unusableTarget && (
            <Alert tone="deny" title={t('clusterLogin.badTarget.title')} live>
              {t('clusterLogin.badTarget.body')}
            </Alert>
          )}

          {state.value.status === 'ready' && state.value.provider !== undefined && (
            <Alert tone="neutral" title={t('clusterLogin.provider.title')}>
              <p className="mono">
                {state.value.provider.kind === 'own'
                  ? state.value.provider.issuer
                  : t('clusterLogin.provider.ours')}
              </p>
              <p style={{ marginTop: 8 }}>
                {state.value.provider.kind === 'provider'
                  ? t('clusterLogin.provider.byProvider')
                  : t('clusterLogin.provider.byTenancy')}
              </p>
              <div style={{ marginTop: 14 }}>
                <Button variant="primary" block disabled={starting} onClick={() => void begin()}>
                  {starting ? `${t('clusterLogin.continuing')}…` : t('clusterLogin.continue')}
                </Button>
              </div>
              {/* Nobody needs the mechanics of the handoff to press the button;
                  somebody wondering what this page keeps meanwhile does. */}
            </Alert>
          )}

          {state.value.status === 'reconfiguring' && (
            <Alert tone="warn" title={t('clusterLogin.reconfiguring.title')} live>
              <p>{t('clusterLogin.reconfiguring.body')}</p>
              <p style={{ marginTop: 8 }}>
                {t('clusterLogin.since', { when: formatRelative(state.value.since) })}
              </p>
              <div style={{ marginTop: 12 }}>
                <Button size="sm" onClick={state.reload}>
                  {t('common.retry')}
                </Button>
              </div>
            </Alert>
          )}

          {state.value.status === 'not_configured' && (
            <Alert tone="warn" title={t('clusterLogin.notConfigured.title')} live>
              {t('clusterLogin.notConfigured.body')}
            </Alert>
          )}

          <RedirectUriNotice value={state.value.redirect_uri} />

          <Alert tone="info" title={t('clusterLogin.account.title')}>
            <p>{t('clusterLogin.account.body')}</p>
            <div style={{ marginTop: 12 }}>
              <a className="btn btn-ghost btn-sm" href={state.value.account_url}>
                <IconExternal size={15} />
                {t('clusterLogin.account.open')}
              </a>
            </div>
          </Alert>

          {USING_STUBS && (
            <>
              <StubClusterProvider />
              <StubBanner />
            </>
          )}
        </div>
      )}
    </AuthLayout>
  );
}

/**
 * A return from the provider that produced no session.
 *
 * Three of these say less than they could, and on purpose. `sign_in_expired` is
 * one code for "no cookie", "unknown key", "too late" and "the state did not
 * match", because to the person in front of it they are one situation and
 * telling them apart tells a guesser which guess was closer. `sign_in_refused`
 * is one code for every check on the identity, for the same reason — the
 * particulars go to the control plane's log, where the tenancy's own operator
 * can read them. And a refusal by policy does not name the group: whoever is
 * asking already knows whose tenancy this is, and the shape of its directory is
 * not theirs to learn from a sign-in screen.
 */
function ReturnFailure({ failure }: { failure: ApiError }) {
  const { t } = useI18n();

  if (failure.code === 'forbidden') {
    return (
      <Alert tone="deny" title={t('clusterLogin.refused.title')} live>
        <p>{t('clusterLogin.refused.body')}</p>
        <p style={{ marginTop: 8 }}>{t('clusterLogin.refused.group')}</p>
      </Alert>
    );
  }
  return <ErrorAlert error={failure} />;
}
